/**
 * Options for a query-list parameter (S10). Fetched through the SAME runTile
 * preview path tiles use (read-only by policy, no persisted tile needed),
 * cached per dashboard load, and re-fetched when an upstream (`dependsOn`)
 * parameter changes OR the definition's query inputs change — the query key
 * carries both. Keyed on the parameter id alone, saving an edited SQL in
 * the settings dialog kept serving the previous query's options.
 */
import { useQuery } from '@tanstack/react-query';
import { getEngineForConnection } from '@/features/connections/utils/connectionLookup';
import type { QueryColumn } from '@/types/queryExecution';
import type { BoundParams, DashboardParameter } from '../../types/dashboardTypes';
import { stableValueKey } from '../../services/dashboardTileCache';
import { deriveTileParameters } from '../../services/parameterBinding';

export type ParamOption = { label: string; value: string };

type QueryListDef = Extract<DashboardParameter, { kind: 'query-list' }>;

/**
 * Resolves a param definition's configured label/value COLUMN NAME (persisted
 * across saves — must keep meaning "the column named X") to the ROW KEY its
 * values actually live under (`col.id ?? col.name`, `col_<i>` for a duplicate
 * or blank name — the same convention as renderHintToChartConfig.ts /
 * columnTypeDetection.ts). Same precedence as `resolveChartColumnRef`: a
 * configured value that IS a column's key wins (so a duplicated name cannot
 * steal it), else a name matching exactly ONE column resolves to that
 * column's key; otherwise falls back to the column at `fallbackIndex` (or the
 * first column) — same positional default the row-read used before this
 * fix. An empty result set (no columns at all) falls back to the configured
 * name itself so a hand-typed name still reads *something* rather than throw.
 */
export function resolveParamOptionColumnKey(
  columns: QueryColumn[],
  configured: string | undefined,
  fallbackIndex: number,
): string {
  const keyOf = (c: QueryColumn): string => c.id ?? c.name;
  if (configured) {
    const byKey = columns.find((c) => keyOf(c) === configured);
    if (byKey) return keyOf(byKey);
    const byName = columns.filter((c) => c.name === configured);
    if (byName.length === 1) return keyOf(byName[0]);
  }
  const fallback = columns[fallbackIndex] ?? columns[0];
  return fallback ? (fallback.id ?? fallback.name) : (configured ?? '');
}

export function useParamOptions(
  dashboardId: string,
  def: QueryListDef,
  allParameters: DashboardParameter[],
  values: BoundParams,
): { options: ParamOption[]; isLoading: boolean; error: string | undefined } {
  const upstream = Object.fromEntries((def.dependsOn ?? []).map((id) => [id, values[id] ?? null]));
  // Every definition field the fetch below reads — a save that changes any
  // of them must land on a fresh entry, not the old query's cached options.
  const queryInputs = {
    connectionId: def.connectionId,
    database: def.database ?? null,
    sql: def.sql,
    labelColumn: def.labelColumn ?? null,
    valueColumn: def.valueColumn ?? null,
  };

  const query = useQuery({
    queryKey: [
      'dashboard-param-options',
      dashboardId,
      def.id,
      stableValueKey(queryInputs),
      stableValueKey(upstream),
    ],
    staleTime: Infinity, // cached per dashboard load; definition/upstream change re-keys
    gcTime: 10 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<ParamOption[]> => {
      const engine = getEngineForConnection(def.connectionId);
      const derived = deriveTileParameters({
        sql: def.sql,
        engine,
        parameters: allParameters,
        values,
      });
      if (!derived.ok) {
        throw new Error(`Waiting for: ${derived.missing.join(', ')}`);
      }
      const executionId = `dash-param-${def.id}-${Date.now()}`;
      const response = await window.electronAPI?.dashboards?.runTile(
        {
          connectionId: def.connectionId,
          database: def.database,
          query: def.sql,
          parameters: derived.parameters,
          maxRows: 500,
          maxBytes: 2 * 1024 * 1024,
          timeoutMs: 15_000,
          trigger: 'preview',
          dashboardId,
          tileId: `param-options-${def.id}`,
        },
        executionId,
        false,
      );
      if (!response || response.kind !== 'result') throw new Error('Options query was blocked');
      if (!response.result.ok) throw new Error(response.result.error);
      const { columns, rows } = response.result.selected;
      // Rows are keyed by ROW KEY, not column name (QueryResultSet contract —
      // a blank/duplicate-named column, e.g. Oracle's `'Visible' AS " "`,
      // lands under `col_<i>`). Explicit mapping by configured name, else
      // first two columns positionally; a single column serves both.
      const labelKey = resolveParamOptionColumnKey(columns, def.labelColumn, 0);
      const valueKey = resolveParamOptionColumnKey(columns, def.valueColumn, columns.length > 1 ? 1 : 0);
      return rows.map((row) => ({
        label: String(row[labelKey] ?? ''),
        value: String(row[valueKey] ?? ''),
      }));
    },
  });

  return {
    options: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : undefined,
  };
}
